'use client'

import { useMounted } from '@/hooks/use-mounted'
import { gaId as configuredGaId } from '@/utils/analytics/gtag'
import { hasLinkSecret } from '@/utils/analytics/redact-url'

import { usePathname } from 'next/navigation'

import { GoogleAnalytics } from '@next/third-parties/google'

import { AnalyticsProvider } from './analytics-provider'
import { WebVitalsReporter } from './web-vitals-reporter'

/**
 * Route prefixes that never report to GA or PostHog.
 *
 * `/e/` is the public evidence link (epic #52). Two reasons it is here, and
 * the privacy one is the reason that would survive the other being fixed:
 *
 *   1. The person on that page is an unauthenticated property contact who
 *      never signed into anything. Beaconing their behaviour to two
 *      third-party analytics products is not something the tokenized-link
 *      model ever asked for.
 *   2. The route carries a strict nonce CSP whose `connect-src` is `'self'`
 *      plus the S3 origin, so those beacons are blocked in the browser
 *      regardless — mounting them only produces console violations and the
 *      false impression that the page is instrumented.
 *
 * Gating happens HERE, on the client, rather than by reading `headers()` in
 * the root layout: `headers()` in a root layout opts every route in the app
 * into dynamic rendering, which is the exact cost the route-scoped CSP was
 * designed to avoid.
 */
const UNREPORTED_PREFIXES = [
  '/e/',
  // The Tier 0 text link a caller opens from our text — the same kind of
  // unauthenticated, tokenized public page, for reason 1 above.
  '/r/'
]

export function AnalyticsMounts() {
  const pathname = usePathname()
  // Nothing mounts until after hydration. The GA id is deploy-time config the
  // browser only learns at runtime (utils/public-env.ts), and the root layout
  // is prerendered at build time — so the server can never render the GA tag.
  // The provider waits too, deliberately: GA renders first, so in the commit
  // where both mount, the GA init script's effect defines `window.gtag` before
  // AnalyticsProvider's one-time config effect runs. Mount the provider any
  // earlier and that config (version/env stamps, send_page_view: false) is a
  // silent no-op, which double-counts page views.
  const mounted = useMounted()

  // Nor while the address bar holds a link secret (an intake link's token, a
  // reset or invite link its page has not yet moved into memory): gtag reads
  // document.location for its own hits — the config page_view, enhanced
  // measurement — and those never pass through our redaction.
  if (
    !mounted ||
    UNREPORTED_PREFIXES.some((prefix) => pathname.startsWith(prefix)) ||
    hasLinkSecret(window.location.href)
  ) {
    return null
  }

  const gaId =
    process.env.NODE_ENV !== 'development' ? configuredGaId() : undefined

  return (
    <>
      {gaId && <GoogleAnalytics gaId={gaId} />}
      <AnalyticsProvider />
      <WebVitalsReporter />
    </>
  )
}
